import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Card, Form, Modal } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";

import SalesLedgerPDF from "../components/SalesLedgerPDF";
import Pagination from "../components/Pagination";
import { PDFViewer } from "@react-pdf/renderer";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { parseApiPagination } from "../utils/parseApiPagination";
import {
  postSalesLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";

const ENTIRE_REPORT_DEFAULT_LIMIT = 20;

function processLedgerEntries(entries) {
  return entries.map((entry) => {
    let formattedDate = entry.invoice_date || entry.date || "-";
    if (formattedDate !== "-" && formattedDate.includes("-")) {
      const dateParts = formattedDate.split(" ")[0].split("-");
      if (dateParts.length === 3) {
        formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      }
    }

    return {
      name: entry.name || entry.customer_name || "-",
      phone:
        entry.contact_number ||
        entry.customer_mobile ||
        entry.user_mobile ||
        entry.phone ||
        entry.mobile ||
        "-",
      user_details: entry.user_details || entry.customer_details || "-",
      invoice_no: entry.invoice_no || entry.voucher_number || "-",
      invoice_date: formattedDate,
      invoice_value: parseFloat(entry.grand_total || 0),
      taxable_value: parseFloat(entry.final_amount || 0),
      sgst: parseFloat(entry.sgst || 0),
      cgst: parseFloat(entry.cgst || 0),
      total_tax: parseFloat(
        entry.gst_amount || entry.total_tax || entry.gst || 0,
      ),
      gold_gm: parseFloat(entry.gold_gm || entry.gold_weight || 0),
      silver_gm: parseFloat(entry.silver_gm || entry.silver_weight || 0),
    };
  });
}

function summarizeLedgerRows(rows) {
  return rows.reduce(
    (acc, entry) => {
      acc.invoice_value += entry.invoice_value;
      acc.taxable_value += entry.taxable_value;
      acc.sgst += entry.sgst;
      acc.cgst += entry.cgst;
      acc.total_tax += entry.total_tax;
      acc.gold_gm += entry.gold_gm;
      acc.silver_gm += entry.silver_gm;
      return acc;
    },
    {
      invoice_value: 0,
      taxable_value: 0,
      sgst: 0,
      cgst: 0,
      total_tax: 0,
      gold_gm: 0,
      silver_gm: 0,
    },
  );
}

function SalesLedger() {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ENTIRE_REPORT_DEFAULT_LIMIT);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  const fetchSalesLedger = useCallback(
    async ({
      page = 1,
      limit = ENTIRE_REPORT_DEFAULT_LIMIT,
      silent = false,
    } = {}) => {
      setLoading(true);

      const apiPayload = {};

      if (entireReport) {
        apiPayload.type = "entire";
        apiPayload.page = page;
        apiPayload.limit = limit;
      } else {
        if (fromDate) apiPayload.from_date = fromDate;
        if (toDate) apiPayload.to_date = toDate;
      }

      try {
        const payload = await postSalesLedger(apiPayload);

        if (
          !payload ||
          (!isErpApiSuccess(payload) && payload.message !== "radha")
        ) {
          toast.error(
            payload?.message || "Failed to fetch ledger data. Please try again.",
          );
          setLedgerRows([]);
          setSummaryData(null);
          setTotalCount(0);
          setHasMorePages(false);
          return;
        }

        const entries = payload.data?.entries || payload.data || [];

        if (!Array.isArray(entries)) {
          toast.error("Invalid ledger response from server.");
          setLedgerRows([]);
          setSummaryData(null);
          setTotalCount(0);
          setHasMorePages(false);
          return;
        }

        if (entries.length === 0) {
          if (!silent) {
            toast.error("No ledger entries found for the selected filters.");
          }
          setLedgerRows([]);
          setSummaryData(null);
          if (entireReport) {
            const pagination = parseApiPagination(payload, 0);
            setCurrentPage(pagination.page);
            setTotalCount(pagination.total);
            setHasMorePages(pagination.hasMore);
          } else {
            setTotalCount(0);
            setHasMorePages(false);
            setCurrentPage(1);
          }
          return;
        }

        const processedEntries = processLedgerEntries(entries);
        const summary = summarizeLedgerRows(processedEntries);

        setLedgerRows(processedEntries);
        setSummaryData(summary);

        const paginationMeta = entireReport
          ? parseApiPagination(payload, processedEntries.length)
          : null;

        if (entireReport && paginationMeta) {
          setCurrentPage(paginationMeta.page);
          setTotalCount(paginationMeta.total);
          setHasMorePages(paginationMeta.hasMore);
          if (paginationMeta.limit) {
            setItemsPerPage(paginationMeta.limit);
          }
        } else {
          setTotalCount(processedEntries.length);
          setHasMorePages(false);
          setCurrentPage(1);
        }

        setPdfData({
          ledgerData: processedEntries,
          summaryData: summary,
          fromDate,
          toDate,
          entireReport,
          currentPage: entireReport ? page : 1,
          totalCount: paginationMeta?.total ?? processedEntries.length,
        });

        if (!silent) {
          toast.success("Ledger generated successfully!");
        }
      } catch (error) {
        toast.error(
          getErpRequestErrorMessage(
            error,
            "An error occurred while fetching the ledger data.",
          ),
        );
        console.error("Error fetching sales ledger:", error);
        setLedgerRows([]);
        setSummaryData(null);
        setTotalCount(0);
        setHasMorePages(false);
      } finally {
        setLoading(false);
      }
    },
    [entireReport, fromDate, toDate],
  );

  const fetchRef = useRef(fetchSalesLedger);
  fetchRef.current = fetchSalesLedger;

  const initialFetchRef = useRef(false);
  // Fetch data on component mount
  useEffect(() => {
    const didRunRef = initialFetchRef.current;
    if (didRunRef) return;
    initialFetchRef.current = true;
    fetchRef.current();
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in SalesLedger, refetching ledger...",
        event.detail,
      );
      fetchRef.current({ page: currentPage, limit: itemsPerPage, silent: true });
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [currentPage, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCurrentPage(1);
    await fetchSalesLedger({ page: 1, limit: itemsPerPage });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchSalesLedger({ page, limit: itemsPerPage, silent: true });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
    fetchSalesLedger({ page: 1, limit, silent: true });
  };

  const handlePrint = () => {
    if (!pdfData || !ledgerRows.length) {
      toast.error("Please generate the ledger first.");
      return;
    }
    setShowPDFModal(true);
  };

  // Memoize the PDF component to avoid unnecessary rerenders
  const pdfViewer = useMemo(() => {
    if (!pdfData) return null;
    return (
      <PDFViewer width="100%" height="100%">
        <SalesLedgerPDF
          ledgerData={pdfData.ledgerData}
          summaryData={pdfData.summaryData}
          fromDate={pdfData.fromDate}
          toDate={pdfData.toDate}
          entireReport={pdfData.entireReport}
        />
      </PDFViewer>
    );
  }, [pdfData]);

  // Format currency with ₹ symbol and commas
  const formatCurrency = (value) => {
    if (!value || value === 0) return "-";
    return `₹ ${Math.round(value).toLocaleString("en-IN")}`;
  };

  // Format currency with decimals
  const formatCurrencyDecimal = (value) => {
    if (!value || value === 0) return "-";
    return `₹ ${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  // Format weight with 3 decimal places
  const formatWeight = (value) => {
    if (!value || value === 0) return "-";
    return value.toFixed(3);
  };

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className={`card ${cardClass}`}>
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Sales Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* From Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => {
                      setEntireReport(e.target.checked);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Sales
                    {entireReport && totalCount > 0 ? (
                      <span className="text-muted fs-6 ms-2">
                        (Page {currentPage} of{" "}
                        {Math.max(1, Math.ceil(totalCount / itemsPerPage))})
                      </span>
                    ) : null}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                  >
                    Print
                  </button>
                </div>
                <div className={`ledger-report-wrap mb-4 ${cardClass}`}>
                  <div className="table-responsive">
                    <table className="table text-nowrap mb-0 align-middle ledger-report-table">
                    <thead>
                      <tr>
                        <th
                          colSpan="2"
                          className="text-center"
                        >
                          User Details
                        </th>
                        <th
                          colSpan="3"
                          className="text-center"
                        >
                          INVOICE DETAILS
                        </th>
                        <th
                          rowSpan="2"
                          className="text-end text-nowrap"
                        >
                          TAXABLE VALUE
                        </th>
                        <th
                          colSpan="3"
                          className="text-center"
                        >
                          AMOUNT OF TAX
                        </th>
                        <th
                          colSpan="2"
                          className="text-center"
                        >
                          WEIGHT
                        </th>
                      </tr>
                      <tr>
                        <th
                          className="text-nowrap"
                        >
                          NAME
                        </th>
                        <th
                          className="text-nowrap"
                        >
                          Phone
                        </th>
                        <th
                          className="text-nowrap"
                        >
                          INVOICE NO
                        </th>
                        <th
                          className="text-nowrap"
                        >
                          INVOICE DATE
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          INVOICE VALUE
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          SGST
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          CGST
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          TOTAL TAX
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          GOLD (GM)
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          SILVER (GM)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerRows.map((row, idx) => (
                        <tr key={idx}>
                          <td
                          >
                            {row.name}
                          </td>
                          <td
                          >
                            {row.phone}
                          </td>
                          <td
                          >
                            {row.invoice_no}
                          </td>
                          <td
                          >
                            {row.invoice_date}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrency(row.invoice_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.taxable_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.sgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.cgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.total_tax)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(row.gold_gm)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(row.silver_gm)}
                          </td>
                        </tr>
                      ))}
                      {/* Summary Row */}
                      {summaryData && (
                        <tr className="ledger-report-summary">
                          <td
                            colSpan="4"
                          ></td>
                          <td
                            className="text-end"
                          >
                            {formatCurrency(summaryData.invoice_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.taxable_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.sgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.cgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.total_tax)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(summaryData.gold_gm)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(summaryData.silver_gm)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
                {entireReport && totalCount > 0 && (
                  <Pagination
                    totalItems={totalCount}
                    hasMore={hasMorePages}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    itemsPerPageOptions={[10, 20, 30, 50, 100]}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => setShowPDFModal(false)}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Sales Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {showPDFModal && pdfViewer}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default SalesLedger;
